import React from "react";

const UNINITIALIZED = Symbol("UNINITIALIZED");

// https://github.com/facebook/react/issues/14490#issuecomment-451924162
function useRefWithInitialFunction<T>(init: () => T) {
  const ref = React.useRef<T | typeof UNINITIALIZED>(UNINITIALIZED);
  if (ref.current === UNINITIALIZED) {
    ref.current = init();
  }
  return ref as React.MutableRefObject<T>;
}

const useStyleTag = () => {
  const styleTag = useRefWithInitialFunction(() => {
    const style = document.createElement("style");
    style.nonce = window.__webpack_nonce__;
    return style;
  });

  React.useLayoutEffect(() => {
    const style = styleTag.current;
    window.requestAnimationFrame(() => {
      document.head.appendChild(style);
    });

    return () => {
      window.requestAnimationFrame(() => {
        style.remove();
      });
    };
  }, [styleTag]);

  const appendCSS = React.useCallback(
    (css: string) => {
      window.requestAnimationFrame(() => {
        styleTag.current.innerHTML += css;
      });
    },
    [styleTag],
  );

  const replaceCSS = React.useCallback(
    (css: string) => {
      window.requestAnimationFrame(() => {
        styleTag.current.innerHTML = css;
      });
    },
    [styleTag],
  );

  const emptyCSS = React.useCallback(() => {
    window.requestAnimationFrame(() => {
      styleTag.current.innerHTML = "";
    });
  }, [styleTag]);

  return { appendCSS, replaceCSS, emptyCSS };
};

export default useStyleTag;
